/** Metric types and value parsing. A metric's type is only what was set in Configure metrics — never guessed. */
import type { MetricKind, Project } from '@/types/models';

export const KINDS: Record<MetricKind, [name: string, description: string]> = {
  asof: ['As of day', 'Count at the end of that day (e.g. 13 in progress). Compare with the previous day.'],
  daily: ['That day only', 'Happened on that day (e.g. 2 certificates generated). Add days to get a total.'],
  todate: ['To date', 'Running total since the start (e.g. 456 acknowledged so far).'],
};

export const isKind = (k: unknown): k is MetricKind => typeof k === 'string' && k in KINDS;

/** The metric's set type, or '' when nobody set it (then: no tag, no total, no change). */
export const kindOf = (p: Pick<Project, 'metricKinds'> | null | undefined, label: string): MetricKind | '' => {
  const k = p?.metricKinds?.[label];
  return isKind(k) ? k : '';
};

export const kindName = (k: MetricKind | ''): string => (k ? KINDS[k][0] : 'Type not set');

/** Plain number from a value such as "1,443"; null for text or empty values. */
export const numOf = (v: unknown): number | null => {
  const n = Number(String(v).replace(/,/g, ''));
  return isNaN(n) || String(v).trim() === '' ? null : n;
};

/** Number + unit from values such as "1,443", "25%", "5.2 days"; null for text such as "25m 59s". */
export const parseMetricValue = (v: unknown): { n: number; u: string } | null => {
  const m = String(v ?? '')
    .trim()
    .replace(/,/g, '')
    .match(/^([-+]?\d*\.?\d+)\s*(%|[a-z]+)?$/i);
  return m ? { n: Number(m[1]), u: m[2] || '' } : null;
};
